import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "../api-client";

export interface FacilitatorCandidate {
  userId: string;
  user: {
    id: string;
    displayName: string | null;
    username: string | null;
    image: string | null;
    email: string;
  } | null;
  activeRoomCount: number;
  atCap: boolean;
}

interface CandidatesResponse {
  candidates: FacilitatorCandidate[];
  perUserCap: number;
}

const CANDIDATES_KEY = ["staff", "facilitator-candidates"] as const;

export function useFacilitatorCandidatesQuery(enabled = true) {
  return useQuery<CandidatesResponse>({
    queryKey: CANDIDATES_KEY,
    enabled,
    queryFn: () =>
      getApiClient().get<CandidatesResponse>(
        "/admin/plotlounge/facilitator-candidates",
      ),
    staleTime: 30 * 1000,
  });
}

export function useAssignFacilitatorMutation(roomId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { userId: string }>({
    mutationFn: ({ userId }) =>
      getApiClient().post(`/admin/plotlounge/rooms/${roomId}/facilitator`, {
        userId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["staff", "plotlounge-rooms", "detail", roomId],
      });
      qc.invalidateQueries({ queryKey: CANDIDATES_KEY });
    },
  });
}

export function useUnassignFacilitatorMutation(roomId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { userId: string }>({
    mutationFn: ({ userId }) =>
      getApiClient().delete(
        `/admin/plotlounge/rooms/${roomId}/facilitator/${userId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["staff", "plotlounge-rooms", "detail", roomId],
      });
      qc.invalidateQueries({ queryKey: CANDIDATES_KEY });
    },
  });
}
